import React, { useState, useEffect } from 'react'
import { stampObjects } from './tools/StampTool'
import styled from 'styled-components'
import { CustomStampCreator } from './CustomStampCreator'

// Group objects by category
const groupedObjects = Object.entries(stampObjects).reduce((acc, [name, obj]) => {
  const category = name.includes('Tree') || name.includes('Bush') || name.includes('Shrub')
    ? 'Plants'
    : 'Furniture'
  
  if (!acc[category]) acc[category] = []
  acc[category].push([name, obj])
  return acc
}, {})

const PaletteContainer = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 240px;
  z-index: 1000;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const PaletteHeader = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CreateButton = styled.button`
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #2563eb;
  }
`

const CustomStampPreview = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  color: white;
  
  ${props => props.shape === 'circle' && `
    border-radius: 50%;
    background-color: ${props.color};
  `}
  
  ${props => props.shape === 'rectangle' && `
    border-radius: 2px;
    background-color: ${props.color};
  `}
`

const PaletteContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
`

const CategoryContainer = styled.div`
  margin-bottom: 12px;
`

const CategoryLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
  padding: 0 4px;
`

const Divider = styled.div`
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 8px 0;
`

const StampButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background-color: ${props => props.selected ? '#e6f7ff' : 'transparent'};
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 4px;
  text-align: left;
  
  &:hover {
    background-color: ${props => props.selected ? '#e6f7ff' : '#f5f5f5'};
  }
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 8px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.5);
`

const StampLabel = styled.span`
  font-size: 13px;
  color: #333;
`

function StampPalette({ onSelectStamp, selectedStamp }) {
  const [showCreator, setShowCreator] = useState(false)
  // State for custom stamps - retrieve from localStorage if available
  const [customStamps, setCustomStamps] = useState({})
  
  // Load custom stamps from localStorage on component mount
  useEffect(() => {
    try {
      const savedStamps = localStorage.getItem('customStamps')
      if (savedStamps) {
        setCustomStamps(JSON.parse(savedStamps))
      }
    } catch (error) {
      console.error('Error loading custom stamps:', error)
    }
  }, [])
  
  // Save custom stamps to localStorage when they change
  useEffect(() => {
    if (Object.keys(customStamps).length > 0) {
      localStorage.setItem('customStamps', JSON.stringify(customStamps))
    }
  }, [customStamps])
  
  // Handler for creating a new custom stamp
  const handleCreateStamp = (newStamp) => {
    const updatedStamps = {
      ...customStamps,
      [newStamp.name]: newStamp
    }
    setCustomStamps(updatedStamps)
    // Automatically select the newly created stamp
    onSelectStamp(newStamp.name, newStamp)
  }
  
  return (
    <>
      <PaletteContainer>
        <PaletteHeader>
          Object Library
          <CreateButton onClick={() => setShowCreator(true)}>
            + Custom
          </CreateButton>
        </PaletteHeader>
        <PaletteContent>
          {/* Built-in stamps */}
          {Object.entries(groupedObjects).map(([category, items]) => (
            <CategoryContainer key={category}>
              <CategoryLabel>{category}</CategoryLabel>
              {items.map(([name, obj]) => (
                <StampButton
                  key={name}
                  selected={selectedStamp === name}
                  onClick={() => onSelectStamp(name)}
                >
                  <IconContainer>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path 
                        d={obj.path} 
                        fill={obj.fill}
                        stroke={selectedStamp === name ? 'rgba(0,0,0,0.2)' : 'none'}
                        strokeWidth="0.5"
                      />
                    </svg>
                  </IconContainer>
                  <StampLabel>{name}</StampLabel>
                </StampButton>
              ))}
              <Divider />
            </CategoryContainer>
          ))}
          
          {/* Custom stamps - show only if there are any */}
          {Object.keys(customStamps).length > 0 && (
            <CategoryContainer>
              <CategoryLabel>Custom Objects</CategoryLabel>
              {Object.entries(customStamps).map(([name, stamp]) => (
                <StampButton
                  key={name}
                  selected={selectedStamp === name}
                  onClick={() => {
                    console.log('Selecting custom stamp:', name, stamp)
                    onSelectStamp(name, stamp)
                  }}
                >
                  <IconContainer>
                    {stamp.isCustom ? (
                      <CustomStampPreview 
                        shape={stamp.shape} 
                        color={stamp.fill}
                        text={stamp.text}
                      >
                        {stamp.text}
                      </CustomStampPreview>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24">
                        <path d={stamp.path} fill={stamp.fill} />
                      </svg>
                    )}
                  </IconContainer>
                  <StampLabel>{name}</StampLabel>
                </StampButton>
              ))}
              <Divider />
            </CategoryContainer>
          )}
        </PaletteContent>
      </PaletteContainer>
      
      {/* Custom Stamp Creator */}
      {showCreator && (
        <CustomStampCreator 
          onClose={() => setShowCreator(false)}
          onCreateStamp={handleCreateStamp}
        />
      )}
    </>
  )
}

export { StampPalette }
